/**
 * Contract + initial-payment sections of the case-approvals detail panel.
 * Extracted from the former monolith. Theme-driven; uses the shared
 * admin/ui primitives.
 */
import { useCallback, useEffect, useState } from "react";
import { Platform, Pressable, Text, View, type ViewStyle } from "react-native";

import {
  formatCents,
  getLatestContractForCase,
  getLatestInitialInvoiceForCase,
  regenerateStripeCheckoutForCase,
  sendContractForCase,
  type CaseForReview,
  type ContractRow,
  type InitialInvoiceRow,
} from "../../../lib/caseApprovals";
import { RADII, STROKES } from "../../../lib/brand";
import { PANE_THEMES } from "../../brand/paneThemes";
import { AdminButton, AdminError, AdminLoading } from "../ui";
import { SubLabel } from "./parts";
import { formatFullTimestamp, formatRelative } from "./helpers";

const THEME = PANE_THEMES.Admin;

const cardStyle = {
  marginTop: 6,
  padding: 14,
  borderRadius: RADII.sm,
  borderWidth: STROKES.hairline,
  borderColor: THEME.border,
  backgroundColor: THEME.surfaceRaisedBg,
} as const;

export function ContractSection({
  competitionId,
  caseStatus,
  hasQuote,
  onContractSent,
}: {
  competitionId: string;
  caseStatus: CaseForReview["status"];
  hasQuote: boolean;
  onContractSent: () => void;
}) {
  const [contract, setContract] = useState<ContractRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setContract(await getLatestContractForCase(competitionId));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load contract status");
    } finally {
      setLoading(false);
    }
  }, [competitionId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function send() {
    setErr(null);
    setSending(true);
    try {
      await sendContractForCase(competitionId);
      await reload();
      onContractSent();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Send failed");
    } finally {
      setSending(false);
    }
  }

  // DocuSign sandbox vs prod is configured server-side; this deep link
  // just opens the generic DocuSign UI so the operator finds the envelope.
  const docusignAppUrl = "https://apps-d.docusign.com/send/documents";

  return (
    <View style={{ marginTop: 18 }}>
      <SubLabel>Contract</SubLabel>

      {loading ? (
        <View style={cardStyle}>
          <AdminLoading theme={THEME} />
        </View>
      ) : caseStatus === "approved" && !contract ? (
        <View style={cardStyle}>
          <Text style={{ fontFamily: THEME.bodyFont, fontSize: 12.5, lineHeight: 18, color: THEME.textOnSurfaceDim }}>
            Case approved, but the DocuSign envelope hasn&apos;t been sent yet (approval normally
            sends it automatically). Click below to send it — the client receives an email from
            DocuSign with a sign link.
          </Text>
          {!hasQuote ? (
            <Text style={{ marginTop: 8, fontFamily: THEME.bodyMediumFont, fontSize: 11.5, color: THEME.destructive }}>
              No quote on file — re-approve the case before sending.
            </Text>
          ) : null}
          {err ? <View style={{ marginTop: 8 }}><AdminError theme={THEME} message={err} /></View> : null}
          <View style={{ marginTop: 12, flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
            <AdminButton theme={THEME} label="Send contract" size="sm" loading={sending} disabled={!hasQuote} onPress={() => void send()} />
            <DisabledHint label="Cancel approval" hint="Reverting approval after contract sent is a manual process" />
          </View>
        </View>
      ) : contract ? (
        <View style={cardStyle}>
          <ContractStatusReadout contract={contract} caseStatus={caseStatus} docusignAppUrl={docusignAppUrl} />
          {err ? <View style={{ marginTop: 8 }}><AdminError theme={THEME} message={err} /></View> : null}
        </View>
      ) : (
        <View style={cardStyle}>
          <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 12, color: THEME.textOnSurfaceDim }}>
            No contract found for this case yet.
          </Text>
        </View>
      )}
    </View>
  );
}

function ContractStatusReadout({
  contract,
  caseStatus,
  docusignAppUrl,
}: {
  contract: ContractRow;
  caseStatus: CaseForReview["status"];
  docusignAppUrl: string;
}) {
  const sentLabel = formatFullTimestamp(contract.sent_at);
  const signedLabel = contract.signed_at ? formatFullTimestamp(contract.signed_at) : null;

  let headline: string;
  let body: string | null = null;
  if (caseStatus === "contract_signed" || (signedLabel && contract.status === "signed")) {
    headline = `Contract signed on ${signedLabel ?? "—"}${contract.signer_name ? ` by ${contract.signer_name}` : ""}.`;
    body = "Awaiting payment link — Stripe integration pending.";
  } else if (contract.status === "declined") {
    headline = `Client declined the envelope${contract.declined_at ? ` on ${formatFullTimestamp(contract.declined_at)}` : ""}.`;
    body = contract.declined_reason ?? "No reason captured — investigate via DocuSign before re-issuing.";
  } else if (contract.status === "voided") {
    headline = "Envelope voided.";
    body = "Issue a new envelope from DocuSign or click Send contract once status returns to 'approved'.";
  } else if (contract.status === "viewed") {
    headline = "Client opened the contract — awaiting signature.";
    body = `Originally sent ${sentLabel} to ${contract.signer_email}.`;
  } else {
    headline = `Contract sent to ${contract.signer_email} at ${sentLabel} — awaiting signature.`;
  }

  return (
    <View>
      <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 13, lineHeight: 19, color: THEME.textOnSurface }}>
        {headline}
      </Text>
      {body ? (
        <Text style={{ marginTop: 6, fontFamily: THEME.bodyFont, fontSize: 12, lineHeight: 18, color: THEME.textOnSurfaceDim }}>
          {body}
        </Text>
      ) : null}
      <View style={{ marginTop: 10, flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
        <AdminButton
          theme={THEME}
          label="View in DocuSign"
          variant="outline"
          size="sm"
          onPress={() => {
            if (Platform.OS === "web") window.open(docusignAppUrl, "_blank");
          }}
        />
      </View>
    </View>
  );
}

export function PaymentSection({ competitionId }: { competitionId: string }) {
  const [invoice, setInvoice] = useState<InitialInvoiceRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      setInvoice(await getLatestInitialInvoiceForCase(competitionId));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to load payment status");
    } finally {
      setLoading(false);
    }
  }, [competitionId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function regenerate() {
    setBusy(true);
    setErr(null);
    try {
      await regenerateStripeCheckoutForCase(competitionId);
      await reload();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to regenerate payment link");
    } finally {
      setBusy(false);
    }
  }

  let summary: string;
  if (loading) {
    summary = "Loading payment status…";
  } else if (!invoice) {
    summary = "Payment link not yet sent.";
  } else {
    const amount = formatCents(invoice.amount_cents);
    if (invoice.status === "pending") {
      summary = `Initial payment of ${amount} — link sent ${formatRelative(invoice.sent_at)}.`;
    } else if (invoice.status === "paid") {
      summary = `Initial payment of ${amount} — paid${invoice.paid_at ? ` on ${formatFullTimestamp(invoice.paid_at)}` : ""}.`;
    } else if (invoice.status === "expired") {
      summary = `Initial payment of ${amount} — link expired.`;
    } else if (invoice.status === "failed") {
      summary = `Initial payment of ${amount} — failed${invoice.failure_reason ? `: ${invoice.failure_reason}` : ""}.`;
    } else if (invoice.status === "refunded") {
      summary = `Initial payment of ${amount} — refunded.`;
    } else {
      summary = `Initial payment of ${amount} — ${invoice.status}.`;
    }
  }

  return (
    <View style={{ marginTop: 18 }}>
      <SubLabel>Payment status</SubLabel>
      <View style={cardStyle}>
        {loading ? (
          <AdminLoading theme={THEME} />
        ) : (
          <>
            <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 13, lineHeight: 19, color: THEME.textOnSurface }}>
              {summary}
            </Text>
            {invoice && invoice.status === "pending" && invoice.hosted_invoice_url ? (
              <Pressable
                onPress={() => {
                  if (Platform.OS === "web" && invoice.hosted_invoice_url && typeof window !== "undefined") {
                    window.open(invoice.hosted_invoice_url, "_blank");
                  }
                }}
                style={[
                  { marginTop: 6, alignSelf: "flex-start" } as ViewStyle,
                  Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                ]}
              >
                <Text style={{ fontFamily: THEME.bodyMediumFont, fontSize: 11.5, color: THEME.accent }}>
                  Open Stripe Checkout →
                </Text>
              </Pressable>
            ) : null}
            {err ? <View style={{ marginTop: 8 }}><AdminError theme={THEME} message={err} /></View> : null}
            <View style={{ marginTop: 12, flexDirection: "row", flexWrap: "wrap", gap: 8 }}>
              <AdminButton theme={THEME} label="Regenerate payment link" size="sm" loading={busy} disabled={invoice?.status === "paid"} onPress={() => void regenerate()} />
            </View>
            {invoice?.status === "paid" ? (
              <Text style={{ marginTop: 8, fontFamily: THEME.bodyFont, fontSize: 11.5, color: THEME.textOnSurfaceDim }}>
                Initial payment already received — regeneration is disabled.
              </Text>
            ) : null}
          </>
        )}
      </View>
    </View>
  );
}

function DisabledHint({ label, hint }: { label: string; hint: string }) {
  return (
    <View
      style={[
        {
          paddingHorizontal: 12,
          paddingVertical: 8,
          borderRadius: RADII.sm,
          borderWidth: STROKES.hairline,
          borderColor: THEME.border,
          opacity: 0.5,
        },
        Platform.OS === "web" ? ({ cursor: "not-allowed", title: hint } as unknown as ViewStyle) : null,
      ]}
      accessibilityRole="button"
      accessibilityState={{ disabled: true }}
      accessibilityHint={hint}
    >
      <Text style={{ fontFamily: THEME.bodyBoldFont, fontSize: 10.5, letterSpacing: 1.4, textTransform: "uppercase", color: THEME.textOnSurfaceDim }}>
        {label}
      </Text>
    </View>
  );
}
